import { DataTable, type DataTableColumn } from "@bruntworkwear-apps/ui";
import { money, quantity } from "@/shared/lib";
import type { CostedLine } from "@/shared/api";
import { customsCell, customsTotalCell, landingCell, landingRateCell, sourceCell } from "./LinesTable.cells";
import styles from "./LinesTable.module.css";
import type { LinesTableProps } from "./LinesTable.types";

const COLUMNS: DataTableColumn<CostedLine>[] = [
  { key: "row", header: "Row", render: (line) => line.row_number, numeric: true, width: "4rem" },
  { key: "receipt", header: "Receipt", render: (line) => line.document_number },
  { key: "po", header: "PO", render: (line) => line.po ?? "—" },
  {
    key: "description",
    header: "Description",
    render: (line) => (
      <span className={styles.description} title={line.description}>
        {line.description || "—"}
      </span>
    ),
  },
  { key: "qty", header: "Qty", render: (line) => quantity(line.quantity), numeric: true },
  {
    key: "landing_rate",
    header: "Landing rate",
    render: landingRateCell,
    numeric: true,
    secondary: true,
  },
  {
    key: "landing",
    header: "Landing cost",
    render: landingCell,
    numeric: true,
  },
  {
    key: "band",
    header: "Rate band",
    render: (line) => line.landing_rate_band || "—",
    secondary: true,
  },
  {
    key: "customs_rate",
    header: "Customs rate",
    render: (line) => customsCell(line, line.customs_rate),
    numeric: true,
    secondary: true,
  },
  {
    key: "customs",
    header: "Customs",
    render: customsTotalCell,
    numeric: true,
  },
  {
    key: "source",
    header: "Rate source",
    render: sourceCell,
    secondary: true,
  },
];

/** Every costed receipt line, with both computed costs. */
export function LinesTable({ lines }: LinesTableProps) {
  const landing = lines.reduce((total, line) => total + (line.landing_cost ?? 0), 0);
  const customs = lines.reduce((total, line) => total + (line.customs_total ?? 0), 0);

  return (
    <DataTable
      rows={lines}
      columns={COLUMNS}
      rowKey={(line) => String(line.row_number)}
      rowTone={(line) =>
        line.is_domestic ? "muted" : line.customs_total === null ? "danger" : "default"
      }
      maxHeight="34rem"
      emptyMessage="No lines were costed. Check the vendor allowlist and the close month."
      footer={
        <span className={styles.footer}>
          <span>{quantity(lines.length)} lines</span>
          <span>Landing {money(landing)}</span>
          <span>Customs {money(customs)}</span>
        </span>
      }
    />
  );
}
